import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError, get, patch, post, type Me } from './api';
import { AvailabilityModal } from './AvailabilityModal';
import { AttendanceModal } from './AttendanceModal';
import { ActivityModal } from './ActivityModal';
import { TimeOffBoxes } from './TimeOff';
import { Confirm, LEVEL_NAMES, LEVELS, Modal, Tip, useToast, type ConfirmSpec } from './ui';

interface Station { id: number; name: string; starOk: boolean }
interface Emp {
  id: number; firstName: string; lastName: string; name: string; phone: string; level: number | null; locationId: number;
  texts: boolean; textsTip: string; smsStop: boolean; customDuties: boolean; maxHours: number | null; maxDays: number | null;
  availabilityLabel: string; hasPin: boolean; pinLocked: boolean;
  archived: { reason: string; from: string } | null; duties: Record<number, 1 | 2>; position: string;
  moving: { to: string; date: string; label: string } | null;
  leaving: { reason: string; date: string; label: string } | null;
  /** late / left early in the last 60 days, not counting "Slow" (owner 10/22) */
  attendance?: { late: string[]; left: string[]; out: string[]; slowLate: string[]; slowLeft: string[]; slowOut: string[] } | null;
}
interface Archived { id: number; name: string; store: string; reason: string; from: string; samePhone: boolean; canRestore: boolean }
interface ListResp { employees: Emp[]; stations: Station[]; kind: 'store' | 'office'; positions: string[]; count: number; canEdit: boolean }

const REASONS: [string, string][] = [['quit', 'Quit'], ['terminated', 'Terminated'], ['seasonal_end', 'Seasonal job ended'], ['other', 'Other']];

/** Splits "First Last Name" into first name and the rest. */
function splitName(full: string): [string, string] {
  const t = full.trim().replace(/\s+/g, ' ');
  const i = t.indexOf(' ');
  return i < 0 ? [t, ''] : [t.slice(0, i), t.slice(i + 1)];
}

/**
 * Employees · <store> (prototype "Employees" tab). Managers type in their own people, so the add form is
 * quick: the mobile number is checked as it's typed, stations can be copied from someone else, "Add
 * another" keeps the level, and the page keeps count.
 */
export function EmployeesPage({ me, locationId, locationName }: { me: Me; locationId: number; locationName: string }) {
  const [data, setData] = useState<ListResp | null>(null);
  const [archived, setArchived] = useState<Emp[] | null>(null);
  const [archQ, setArchQ] = useState('');
  const [err, setErr] = useState('');
  const [confirm, setConfirm] = useState<ConfirmSpec | null>(null);
  const [codeShown, setCodeShown] = useState<{ name: string; code: string; minutes: number } | null>(null);
  const [archiving, setArchiving] = useState<Emp | null>(null);
  const [transferring, setTransferring] = useState<Emp | null>(null);
  const [availFor, setAvailFor] = useState<number | null>(null);
  const [attFor, setAttFor] = useState<number | null>(null);
  const [actFor, setActFor] = useState<number | null>(null);
  const [toast, showToast] = useToast();

  const load = useCallback(async () => {
    try { setData(await get<ListResp>(`/api/employees?location=${locationId}`)); setErr(''); }
    catch (e) { setErr(e instanceof ApiError ? e.message : 'Can’t load employees.'); }
  }, [locationId]);
  useEffect(() => { setData(null); setArchived(null); void load(); }, [load]);

  const ro = !data?.canEdit;
  const replace = (e: Emp) => setData((d) => d && { ...d, employees: d.employees.map((x) => (x.id === e.id ? { ...x, ...e } : x)) });
  const act = async (fn: () => Promise<void>) => {
    try { await fn(); } catch (e) { showToast(e instanceof ApiError ? e.message : 'That didn’t save. Try again.'); }
  };

  const loadArchived = async () => {
    const r = await get<ListResp>(`/api/employees?location=${locationId}&archived=true`);
    setArchived(r.employees.filter((e) => e.archived));
  };

  // The archived list's search box: name or mobile number; newest archived first.
  const q = archQ.toLowerCase().trim(); const qd = q.replace(/\D/g, '');
  const archShown = (archived ?? [])
    .filter((a) => !q || a.name.toLowerCase().includes(q) || (qd.length >= 3 && a.phone.replace(/\D/g, '').includes(qd)))
    .sort((a, b) => (b.archived?.from ?? '').localeCompare(a.archived?.from ?? ''));

  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <h1 style={{ margin: 0, fontSize: 24, fontWeight: 600 }}>Employees · {locationName}</h1>
        <div style={{ fontSize: 14, color: '#4A5561' }}>{data?.kind === 'office'
          ? 'Techs and office staff. Only admins manage this list. Each person has a position here; their store level decides which stations they can work when they fill in at a store.'
          : 'Admins and location managers can add employees, fix a name or mobile number (just click it and type), choose which stations each person can work, and archive people. A level ticks a standard set of boxes; change any box to suit the person.'}</div>
      </div>
      {err && <div className="errbar">{err}</div>}

      {data && data.kind === 'store' && <TimeOffBoxes locationId={locationId} />}
      {data && !ro && <AddForm locationId={locationId} locationName={locationName} people={data.employees} me={me} count={data.count} office={data.kind === 'office'} positions={data.positions}
        onAdded={(e, count) => { setData((d) => d && { ...d, count, employees: [...d.employees, e].sort((a, b) => a.name.localeCompare(b.name)) }); }} />}

      {data && (
        <>
          {data.kind !== 'office' && <div style={{ fontSize: 13, color: '#4A5561' }}>Click a box to tick it (✓). For a station whose first spot needs a star, click again for ★; once more to clear. Changing a box by hand makes the level C (custom); picking a level resets the boxes.</div>}
          <datalist id="mw-positions">{data.positions.map((p) => <option key={p} value={p} />)}</datalist>
          <div style={{ overflowX: 'auto', border: '1px solid #C9CEC6', borderRadius: 8, background: '#FFFFFF' }}>
            <table className="tbl dtbl">
              <thead><tr>
                <th className="stick">Name</th>
                {data.kind === 'office' && <th>Position</th>}
                {data.kind === 'office'
                  ? <th>Store level<Tip text="Only used when this person fills in at a store: it decides which stations they can work there, the same as for store employees." /></th>
                  : <th>Level<Tip text="The level ticks a standard set of duty boxes (set by admins in Settings). Change any box by hand and the level shows C (custom). Picking a level again resets the boxes to that level’s standard set." /></th>}
                {data.stations.map((s) => <th key={s.id} className="vth"><span className="vbtn" style={{ cursor: 'default' }}>{s.name}</span></th>)}
                <th>{data.kind === 'office' ? 'Mobile number' : 'Mobile'}</th>
                <th />
              </tr></thead>
              <tbody>
                {data.employees.map((e) => (
                  <EmpRow key={e.id} e={e} stations={data.stations} ro={ro} me={me} office={data.kind === 'office'}
                    onChange={replace} act={act} setConfirm={setConfirm} setCodeShown={setCodeShown} setArchiving={setArchiving} setTransferring={me.role === 'admin' ? setTransferring : undefined} openAvail={() => setAvailFor(e.id)} openAtt={() => setAttFor(e.id)} openAct={me.role === 'admin' ? () => setActFor(e.id) : undefined} />
                ))}
                {data.employees.length === 0 && (
                  <tr><td colSpan={data.stations.length + 5} style={{ color: '#4A5561' }}>No one yet. Add your first employee above.</td></tr>
                )}
              </tbody>
            </table>
          </div>
          {data.kind !== 'office' && <div style={{ fontSize: 13, color: '#4A5561' }}>✓ can work it · ★ can also take the first spot where a station needs it (e.g. lead kart operator) · Level C = boxes changed by hand</div>}
          <div className="archbox">
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <button className="btn2" style={{ minHeight: 32, padding: '2px 12px' }}
                onClick={() => (archived ? setArchived(null) : void act(loadArchived))}>{archived ? 'Hide archived' : 'Show archived'}</button>
              <span style={{ fontSize: 13, color: '#4A5561' }}>People who left, kept with their history. Restore puts them back on the list.</span>
            </div>
            {archived && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {archived.length > 0 && <input className="inp" type="search" value={archQ} onChange={(x) => setArchQ(x.target.value)}
                  placeholder="Search archived by name or mobile number" aria-label="Search archived employees" style={{ maxWidth: 360, minHeight: 36 }} />}
                {archived.length === 0 && <div style={{ fontSize: 13, color: '#4A5561' }}>No one archived.</div>}
                {archived.length > 0 && archShown.length === 0 && <div style={{ fontSize: 13, color: '#4A5561' }}>No one archived matches “{archQ.trim()}”.</div>}
                {archShown.map((a) => (
                  <div key={a.id} className="chg"><b style={{ fontWeight: 600 }}>{a.name}</b>
                    <span style={{ color: '#4A5561' }}>{REASONS.find((r) => r[0] === a.archived?.reason)?.[1] ?? ''} · off the schedule from {a.archived?.from}</span>
                    <span style={{ flex: '1 1 auto' }} />
                    <button className="btn2" disabled={ro} style={{ minHeight: 28, padding: '2px 10px' }} onClick={() => act(async () => {
                      await post(`/api/employees/${a.id}/restore`); await loadArchived(); await load(); showToast(`${a.name} is back on the list.`);
                    })}>Restore</button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {confirm && <Confirm spec={confirm} onClose={() => setConfirm(null)} />}
      {codeShown && (
        <Modal title={`Sign-in code for ${codeShown.name}`} onClose={() => setCodeShown(null)}>
          <div className="codebig">{codeShown.code}</div>
          <div style={{ fontSize: 14, lineHeight: 1.5 }}>Give {codeShown.name} this code. On their phone they open <b>mywunderland.com</b>, type their mobile number, then this code, and pick their own PIN. It works once, for {codeShown.minutes} minutes.</div>
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}><button className="tbtn primary" onClick={() => setCodeShown(null)}>Done</button></div>
        </Modal>
      )}
      {transferring && <TransferModal e={transferring} fromLocationId={locationId} fromName={locationName} onClose={() => setTransferring(null)}
        onDone={async (msg) => { setTransferring(null); await load(); showToast(msg); }} />}
      {archiving && <ArchiveModal e={archiving} onClose={() => setArchiving(null)} onDone={async (msg: string) => { setArchiving(null); await load(); if (archived) await loadArchived(); showToast(msg); }} />}
      {attFor !== null && <AttendanceModal employeeId={attFor} onClose={() => setAttFor(null)} />}
      {actFor !== null && <ActivityModal employeeId={actFor} onClose={() => setActFor(null)} />}
      {availFor !== null && <AvailabilityModal<Emp> employeeId={availFor} onClose={() => setAvailFor(null)} onChange={replace} />}
      {toast}
    </section>
  );
}

/** The add form at the top: name, level, mobile number (checked as typed), copy stations from. */
function AddForm({ locationId, locationName, people, me, count, onAdded, office, positions }: {
  locationId: number; locationName: string; people: Emp[]; me: Me; count: number; onAdded: (e: Emp, count: number) => void; office: boolean; positions: string[];
}) {
  const [position, setPosition] = useState('');
  const [name, setName] = useState('');
  const [level, setLevel] = useState(3);
  const [phone, setPhone] = useState('');
  const [copyFrom, setCopyFrom] = useState<number | ''>('');
  const [phoneNote, setPhoneNote] = useState<{ ok: boolean; text: string } | null>(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [added, setAdded] = useState<string[]>([]);
  const [byName, setByName] = useState<Archived[]>([]);
  const [byPhone, setByPhone] = useState<Archived[]>([]);
  const nameRef = useRef<HTMLInputElement>(null);
  const canAddManagers = me.role === 'admin' || (me.level ?? 0) >= 9;

  // As-you-type check: wait until 10 digits, then ask the server whether anyone has the number.
  useEffect(() => {
    const d = phone.replace(/\D/g, '');
    if (!d) { setPhoneNote(null); return; }
    if (d.length < 10) { setPhoneNote({ ok: false, text: `${10 - d.length} more digit${10 - d.length === 1 ? '' : 's'}` }); return; }
    const t = setTimeout(() => {
      get<{ ok: boolean; problem: string | null; formatted: string | null; archived?: Archived[] }>(`/api/employees/check-phone?phone=${encodeURIComponent(phone)}`)
        .then((r) => { setPhoneNote(r.ok ? { ok: true, text: `✓ ${r.formatted}` } : { ok: false, text: r.problem ?? '' }); setByPhone(r.archived ?? []); })
        .catch(() => setPhoneNote(null));
    }, 250);
    return () => clearTimeout(t);
  }, [phone]);
  useEffect(() => { if (phone.replace(/\D/g, '').length < 10) setByPhone([]); }, [phone]);

  // A rehire? Look for an archived person with exactly this name once a first and last name are typed.
  useEffect(() => {
    const [first, last] = splitName(name);
    if (!first || !last) { setByName([]); return; }
    const t = setTimeout(() => {
      get<{ archived: Archived[] }>(`/api/employees/check-name?first=${encodeURIComponent(first)}&last=${encodeURIComponent(last)}`)
        .then((r) => setByName(r.archived)).catch(() => setByName([]));
    }, 300);
    return () => clearTimeout(t);
  }, [name]);
  const rehires = [...byPhone, ...byName.filter((n) => !byPhone.some((p) => p.id === n.id))];
  const restore = async (a: Archived) => {
    setBusy(true); setErr('');
    try {
      const r = await post<{ employee: Emp }>(`/api/employees/${a.id}/restore`);
      if (r.employee.locationId === locationId) onAdded(r.employee, count + 1);
      setAdded((x) => [`${r.employee.name} (restored${r.employee.locationId === locationId ? '' : ` at ${a.store}`})`, ...x].slice(0, 5));
      setName(''); setPhone(''); setPhoneNote(null); setPosition(''); setByName([]); setByPhone([]);
      nameRef.current?.focus();
    } catch (e) { setErr(e instanceof ApiError ? e.message : 'That didn’t work. Try again.'); }
    finally { setBusy(false); }
  };

  const phoneBad = !!phone.trim() && !phoneNote?.ok;
  const add = async () => {
    const [firstName, lastName] = splitName(name);
    setBusy(true); setErr('');
    try {
      const r = await post<{ employee: Emp; count: number }>('/api/employees', {
        locationId, firstName, lastName, phone: phone.trim() || null, level, copyStationsFrom: office ? null : copyFrom || null,
        position: office ? position.trim() || null : null,
      });
      onAdded(r.employee, r.count);
      setAdded((a) => [r.employee.name, ...a].slice(0, 5));
      // "Add another": keep the store, level and copy-from; clear the person.
      setName(''); setPhone(''); setPhoneNote(null); setPosition(''); setByName([]); setByPhone([]);
      nameRef.current?.focus();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'That didn’t save. Try again.');
    } finally { setBusy(false); }
  };

  return (
    <div style={{ background: '#FFFFFF', border: '1px solid #C9CEC6', borderRadius: 8, padding: '16px 20px', display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-start' }}>
      <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13, color: '#4A5561' }}>Name
        <input ref={nameRef} className="inp" value={name} onChange={(e) => setName(e.target.value)} placeholder="First and last name" style={{ width: 240 }}
          onKeyDown={(e) => { if (e.key === 'Enter' && name.trim() && !phoneBad) void add(); }} />
        <span className="fieldnote" />
      </label>
      {office && (
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13, color: '#4A5561' }}>Position
          <input className="inp" list="mw-positions" value={position} onChange={(e) => setPosition(e.target.value)} placeholder="e.g. Tech" style={{ width: 160 }} />
          <span className="fieldnote">{positions.length ? 'Pick one or type a new one' : ''}</span>
        </label>
      )}
      <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13, color: '#4A5561' }}>{office ? 'Store level (when filling in)' : 'Level'}
        <select value={level} onChange={(e) => setLevel(Number(e.target.value))}>
          {LEVELS.filter((v) => v < 7 || canAddManagers).map((v) => <option key={v} value={v}>{LEVEL_NAMES[v]}</option>)}
        </select>
        <span className="fieldnote" />
      </label>
      <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13, color: '#4A5561' }}>Mobile number
        <input className="inp" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="503-555-0100" style={{ width: 160 }}
          onKeyDown={(e) => { if (e.key === 'Enter' && name.trim() && !phoneBad) void add(); }} />
        <span className={'fieldnote' + (phoneNote ? (phoneNote.ok ? ' good' : ' bad') : '')} style={{ maxWidth: 260 }}>{phoneNote?.text ?? 'What they’ll sign in with'}</span>
      </label>
      {!office && <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13, color: '#4A5561' }}>Copy stations from
        <select value={copyFrom} onChange={(e) => setCopyFrom(e.target.value ? Number(e.target.value) : '')}>
          <option value="">— use their level —</option>
          {people.filter((p) => !p.archived).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        <span className="fieldnote" />
      </label>}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, paddingTop: 19 }}>
        <button className="btn" onClick={add} disabled={busy || !name.trim() || phoneBad}>Add employee</button>
        <span className="fieldnote" />
      </div>
      {rehires.map((a) => (
        <div key={a.id} className="rehire" role="status">
          {a.canRestore
            ? <span><b>⚠ Is this the same person?</b> {a.name} was archived at {a.store} on {a.from} ({a.reason}){a.samePhone ? ', with this mobile number' : ''}. If you’re rehiring them, restore them to keep their history. If this is someone new, just carry on.</span>
            : <span><b>⚠ Is this the same person?</b> {a.name} used to work at {a.store} and was archived on {a.from} ({a.reason}){a.samePhone ? ', with this mobile number' : ''}. <b>Only an admin can bring back someone from another store</b>, so ask an admin to restore {a.name.split(' ')[0]} and move them to {locationName}. That way they keep their history (past shifts, time off, text agreement). Please don’t add them again as a new person, or they’ll have two records. If this is someone new, just carry on.</span>}
          {a.canRestore && <button className="btn2" disabled={busy} onClick={() => void restore(a)} style={{ minHeight: 32, padding: '2px 12px', flexShrink: 0 }}>Restore {a.name.split(' ')[0]} instead</button>}
        </div>
      ))}
      <div style={{ flexBasis: '100%', display: 'flex', flexWrap: 'wrap', gap: '4px 16px', alignItems: 'baseline' }}>
        <span className="addcount">{count} {count === 1 ? 'person' : 'people'} on the list</span>
        {added.length > 0 && <span style={{ fontSize: 13, color: '#4A5561' }}>Just added: {added.join(', ')}. The form stays ready for the next person.</span>}
        {err && <span className="fieldnote bad" style={{ fontSize: 13 }}>{err}</span>}
      </div>
    </div>
  );
}

/** "Attendance" (owner 10/22): a red number for late / left early / called out, a blue one for slow cuts (last 60 days). */
function AttendanceButton({ e, onOpen }: { e: Emp; onOpen: () => void }) {
  const a = e.attendance;
  const own = a ? a.late.length + a.left.length + a.out.length : 0, slow = a ? a.slowLate.length + a.slowLeft.length + a.slowOut.length : 0;
  const tip = own || slow ? [a!.late.length ? `Late ${a!.late.length}×` : '', a!.left.length ? `Left early ${a!.left.length}×` : '', a!.out.length ? `Called out ${a!.out.length}×` : '', slow ? `Cut for slow ${slow}×` : ''].filter(Boolean).join(' · ') + ' in the last 60 days'
    : 'Nothing in the last 60 days';
  return (
    <button className={own ? 'attmini atthas' : slow ? 'attmini atthas slowonly' : 'attmini'} onClick={onOpen} title={tip} aria-label={`${e.name}’s attendance: ${tip}`}>
      Attendance{own > 0 && <span className="attbadge">{own}</span>}{slow > 0 && <span className="attbadge slow">{slow}</span>}
    </button>
  );
}

function EmpRow({ e, stations, ro, me, office, onChange, act, setConfirm, setCodeShown, setArchiving, setTransferring, openAvail, openAtt, openAct }: {
  e: Emp; stations: Station[]; ro: boolean; me: Me; office: boolean;
  onChange: (e: Emp) => void; act: (fn: () => Promise<void>) => Promise<void>;
  setConfirm: (c: ConfirmSpec) => void; setCodeShown: (c: { name: string; code: string; minutes: number }) => void; setArchiving: (e: Emp) => void; setTransferring?: (e: Emp) => void; openAvail: () => void; openAtt: () => void; openAct?: () => void;
}) {
  const [name, setName] = useState(e.name);
  const [phone, setPhone] = useState(e.phone);
  const [position, setPosition] = useState(e.position);
  useEffect(() => { setName(e.name); setPhone(e.phone); setPosition(e.position); }, [e.name, e.phone, e.position]);
  const savePosition = () => act(async () => {
    if (position.trim() === e.position) return;
    onChange((await patch<{ employee: Emp }>(`/api/employees/${e.id}`, { position: position.trim() || null })).employee);
  });
  const canSetManagers = me.role === 'admin' || (me.level ?? 0) >= 9;
  const levelLocked = !canSetManagers && (e.level ?? 0) >= 7;

  const saveName = () => act(async () => {
    const [firstName, lastName] = splitName(name);
    if (!firstName) { setName(e.name); return; }
    if (`${firstName} ${lastName}`.trim() === e.name) return;
    onChange((await patch<{ employee: Emp }>(`/api/employees/${e.id}`, { firstName, lastName })).employee);
  });
  const savePhone = () => act(async () => {
    if (phone.trim() === e.phone) return;
    try { onChange((await patch<{ employee: Emp }>(`/api/employees/${e.id}`, { phone: phone.trim() || null })).employee); }
    catch (err) { setPhone(e.phone); throw err; }
  });
  const clickDuty = (st: Station) => act(async () => {
    const v = e.duties?.[st.id] ?? 0;
    const next = v === 0 ? 1 : v === 1 && st.starOk ? 2 : 0;
    onChange((await post<{ employee: Emp }>(`/api/employees/${e.id}/duty`, { stationId: st.id, value: next })).employee);
  });
  const toggleTexts = () => {
    if (e.texts) { void act(async () => onChange((await post<{ employee: Emp }>(`/api/employees/${e.id}/texts`, { on: false })).employee)); return; }
    setConfirm({
      title: `Turn on texts for ${e.name}?`,
      text: `Only do this if ${e.firstName} has told you they agree to the Wunderland text message terms (the program description, opt-out and help wording registered for our texts). If they haven’t, ask them to turn texts on themselves in the app. This will be recorded as set by ${me.name}.${e.smsStop ? ` ${e.firstName} replied STOP before, so they also need to text START to our number once.` : ''}`,
      ok: 'Yes, they agreed',
      run: () => act(async () => onChange((await post<{ employee: Emp }>(`/api/employees/${e.id}/texts`, { on: true, employeeAgreed: true })).employee)),
    });
  };
  const signButton = () => {
    if (!e.hasPin) {
      void act(async () => setCodeShown(await post<{ name: string; code: string; minutes: number }>(`/api/employees/${e.id}/sign-in-code`)));
      return;
    }
    setConfirm({
      title: `Reset ${e.name}’s PIN?`,
      text: `Their PIN is removed and they’re signed out. Next time they sign in they get a new code (by text, or from you) and pick a new PIN.`,
      ok: 'Reset PIN',
      run: () => act(async () => onChange((await post<{ employee: Emp }>(`/api/employees/${e.id}/reset-pin`)).employee)),
    });
  };

  return (
    <tr>
      <td className="stick" style={{ whiteSpace: 'nowrap' }}>
        <input className="inp ninp" value={name} disabled={ro} aria-label={`Name of ${e.name}`}
          onChange={(x) => setName(x.target.value)} onBlur={saveName} onKeyDown={(x) => { if (x.key === 'Enter') (x.target as HTMLInputElement).blur(); }} />
        <div style={{ lineHeight: 0 }}><AttendanceButton e={e} onOpen={openAtt} />{openAct && <button className="attmini" style={{ marginLeft: 4 }} onClick={openAct} title={`${e.name}’s sign-in activity (admins only)`}>Activity</button>}</div>
        {e.leaving && <span className="movetag leavetag" title={`Archived (${e.leaving.reason}): ${e.firstName} stays on this list and can sign in until ${e.leaving.date}, then comes off. Their shifts from ${e.leaving.date} on were taken off the schedule.`}>{e.leaving.label}</span>}
        {e.moving && <span className="movetag" title={`Transferred by an admin: ${e.firstName} stays on this list until ${e.moving.date}, then moves to ${e.moving.to}. Shifts already on a schedule stay where they are.`}>{e.moving.label}</span>}
      </td>
      {office && (
        <td>
          <input className="inp pinp" list="mw-positions" value={position} disabled={ro} aria-label={`Position of ${e.name}`} placeholder="add position"
            onChange={(x) => setPosition(x.target.value)} onBlur={savePosition} onKeyDown={(x) => { if (x.key === 'Enter') (x.target as HTMLInputElement).blur(); }} />
        </td>
      )}
      <td>
        <select className="sel-sm" value={e.customDuties ? 'C' : String(e.level ?? 0)} disabled={ro || levelLocked} aria-label={`Level for ${e.name}`}
          onChange={(x) => { if (x.target.value !== 'C') void act(async () => onChange((await patch<{ employee: Emp }>(`/api/employees/${e.id}`, { level: Number(x.target.value) })).employee)); }}>
          <option value="C">C · Custom</option>
          {LEVELS.filter((v) => v < 7 || canSetManagers || v === e.level).map((v) => <option key={v} value={v}>{LEVEL_NAMES[v]}</option>)}
        </select>
      </td>
      {stations.map((st) => {
        const v = e.duties?.[st.id] ?? 0;
        return (
          <td key={st.id} className="dtd">
            <button className={'dcell' + (v ? ' on' : '') + (v === 2 ? ' star' : '')} disabled={ro} onClick={() => clickDuty(st)}
              aria-label={`${e.name}, ${st.name}: ${v === 2 ? 'yes, with star' : v ? 'yes' : 'no'}`}>{v === 2 ? '★' : v ? '✓' : ''}</button>
          </td>
        );
      })}
      <td>
        <input className="inp pinp" value={phone} disabled={ro} aria-label={`Mobile number for ${e.name}`} placeholder="add number"
          onChange={(x) => setPhone(x.target.value)} onBlur={savePhone} onKeyDown={(x) => { if (x.key === 'Enter') (x.target as HTMLInputElement).blur(); }} />
        <label className="allday" style={{ fontSize: 11, color: '#4A5561' }} title={e.textsTip}>
          <input type="checkbox" checked={e.texts} disabled={ro} onChange={toggleTexts} />Texts
        </label>
      </td>
      <td style={{ whiteSpace: 'nowrap' }}>
        <span style={{ display: 'inline-flex', gap: 6 }}>
          {!ro && <button className="btn2" style={{ minHeight: 30, padding: '2px 10px' }} onClick={signButton}
            title={e.hasPin ? `${e.name} has a PIN. Reset it if they forgot it.` : `Shows a one-time code to give ${e.firstName}.`}>
            {e.pinLocked ? 'Reset PIN (locked)' : e.hasPin ? 'Reset PIN' : 'Sign-in code'}</button>}
          <button className="btn2" style={{ minHeight: 30, padding: '2px 10px' }} onClick={openAvail} title={`See ${e.name}’s availability`}>
            Availability{e.availabilityLabel ? ` ${e.availabilityLabel}` : ''}</button>
          {!ro && setTransferring && <button className="btn2" style={{ minHeight: 30, padding: '2px 10px' }} onClick={() => setTransferring(e)} aria-label={`Transfer ${e.name} to another location`} title="Admins only">Transfer</button>}
          {!ro && !e.leaving && <button className="btn2" style={{ minHeight: 30, padding: '2px 10px' }} onClick={() => setArchiving(e)}>Archive</button>}
          {!ro && e.leaving && <button className="btn2" style={{ minHeight: 30, padding: '2px 10px' }} title={`${e.firstName} stays on the list. Shifts already taken off the schedule aren’t put back.`}
            onClick={() => setConfirm({ title: `Cancel ${e.name}’s archive?`, text: `${e.firstName} stays on the list and won’t come off on ${e.leaving!.date}. Shifts that were taken off the schedule aren’t put back; add them again on the day board if needed.`, ok: 'Cancel archive',
              run: async () => { await act(async () => { const r = await post<{ employee: Emp }>(`/api/employees/${e.id}/restore`); onChange(r.employee); }); } })}>Cancel archive</button>}
        </span>
      </td>
    </tr>
  );
}

/** Transfer (admins only): they move to the other location from the date picked. */
function TransferModal({ e, fromLocationId, fromName, onClose, onDone }: { e: Emp; fromLocationId: number; fromName: string; onClose: () => void; onDone: (msg: string) => Promise<void> }) {
  const [locs, setLocs] = useState<{ id: number; name: string; kind: string }[]>([]);
  const [to, setTo] = useState('');
  const [from, setFrom] = useState(() => new Date().toLocaleDateString('en-CA'));
  const [err, setErr] = useState('');
  const [sure, setSure] = useState(false);
  useEffect(() => { get<{ locations: { id: number; name: string; kind: string }[] }>('/api/locations').then((r) => setLocs(r.locations.filter((l) => l.id !== fromLocationId))).catch(() => setLocs([])); }, [fromLocationId]);
  const toName = locs.find((l) => String(l.id) === to)?.name ?? '';
  const nice = from ? new Date(from + 'T12:00:00').toLocaleDateString([], { weekday: 'long', month: 'numeric', day: 'numeric' }) : '';
  return (
    <Modal title={sure ? `Transfer ${e.name} to ${toName}?` : `Transfer ${e.name}`} onClose={onClose}>
      {!sure ? <>
        <div style={{ fontSize: 14, lineHeight: 1.5 }}>They become part of the new location’s employee list and their hours count there from the date you pick. Hours before that date stay with {fromName}. Shifts already on a schedule stay where they are (they show as shared), so nothing disappears.</div>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13, color: '#4A5561' }}>Move to
          <select value={to} onChange={(x) => setTo(x.target.value)} aria-label="Move to">
            <option value="">Choose a location</option>
            {locs.map((l) => <option key={l.id} value={l.id}>{l.name}{l.kind === 'office' ? ' (office)' : ''}</option>)}
          </select>
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13, color: '#4A5561' }}>Starting
          <input className="inp" type="date" value={from} onChange={(x) => setFrom(x.target.value)} />
        </label>
      </> : (
        <div style={{ fontSize: 14, lineHeight: 1.5 }}>{e.name} moves from {fromName} to {toName} starting {nice}. Their hours count at {toName} from then on, and they leave {fromName}’s employee list{from > new Date().toLocaleDateString('en-CA') ? ' on that day' : ''}. Stations ticked by hand carry over to {toName}’s stations with the same name. Shifts already on a schedule stay where they are.</div>
      )}
      {err && <div className="fieldnote bad">{err}</div>}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        <button className="btn2" onClick={sure ? () => setSure(false) : onClose}>{sure ? 'Back' : 'Cancel'}</button>
        <button className="tbtn primary" disabled={!to || !from} onClick={async () => {
          if (!sure) { setSure(true); return; }
          try { const r = await post<{ message: string }>(`/api/employees/${e.id}/transfer`, { toLocationId: Number(to), from }); await onDone(`${e.name} will move to ${toName} starting ${nice}.`); void r; }
          catch (x) { setErr(x instanceof ApiError ? x.message : 'That didn’t save.'); setSure(false); }
        }}>{sure ? 'Transfer' : 'Transfer…'}</button>
      </div>
    </Modal>
  );
}

function ArchiveModal({ e, onClose, onDone }: { e: Emp; onClose: () => void; onDone: (msg: string) => Promise<void> }) {
  const [reason, setReason] = useState('');
  const [from, setFrom] = useState(() => new Date().toLocaleDateString('en-CA'));
  const [err, setErr] = useState('');
  return (
    <Modal title={`Archive ${e.name}?`} onClose={onClose}>
      <div style={{ fontSize: 14, lineHeight: 1.5 }}>They come off every schedule from the date you pick, and their shifts from that day on are removed. If the date is later on, they stay on the list and can sign in until then (shown as “Leaving 11/15”). Their history is kept, and you can restore them any time.</div>
      <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13, color: '#4A5561' }}>Reason
        <select value={reason} onChange={(x) => setReason(x.target.value)}>
          <option value="">Pick one…</option>
          {REASONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      </label>
      <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13, color: '#4A5561' }}>Off the schedule from
        <input className="inp" type="date" value={from} onChange={(x) => setFrom(x.target.value)} />
      </label>
      {err && <div className="fieldnote bad">{err}</div>}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        <button className="btn2" onClick={onClose}>Cancel</button>
        <button className="tbtn primary" disabled={!reason || !from} onClick={async () => {
          try {
            const r = await post<{ later: boolean; removedShifts: number }>(`/api/employees/${e.id}/archive`, { reason, from });
            const nice = new Date(from + 'T12:00:00').toLocaleDateString([], { month: 'numeric', day: 'numeric' });
            await onDone((r.later ? `${e.name} will come off the list on ${nice}.` : `${e.name} was archived.`)
              + (r.removedShifts ? ` ${r.removedShifts} shift${r.removedShifts === 1 ? '' : 's'} from ${nice} on came off the schedule.` : ''));
          }
          catch (x) { setErr(x instanceof ApiError ? x.message : 'That didn’t save.'); }
        }}>Archive</button>
      </div>
    </Modal>
  );
}
